<template>
  <div class="flex flex-col">
    <CMSProductListingPageTeaser
      :category-id="currentCategoryId"
      :slug="pathWithoutLocale"
      class="w-full"
    />

    <div
      class="container relative flex rounded-t-2xl bg-white pt-4 max-sm:max-w-none md:rounded-none md:pt-8"
    >
      <SFCategorySideNavigation
        v-if="rootCategories?.length"
        :current-category="currentCategory"
        :fetching-categories="fetchingCategories"
        :root-categories="rootCategories"
        class="sticky top-8 h-full max-md:hidden sm:min-w-40 md:min-w-[14.75rem] lg:min-w-[17rem]"
      />

      <div class="w-full grow">
        <div class="flex flex-wrap items-center justify-between gap-5">
          <div class="flex items-center">
            <SFCategoryNavigationBackButton
              :current-category="currentCategory"
              class="mr-2 md:hidden"
            />
            <SFCategoryBreadcrumbs
              v-if="currentCategory"
              :category="currentCategory"
              :products-count="totalProductsCount"
              :products-fetching="productsStatus"
            />
          </div>
          <div class="hidden gap-4 md:flex">
            <SFSortSelection
              v-if="currentCategory"
              :category="currentCategory"
              class="max-sm:hidden"
            />
            <SFFilterToggleButton :label="$t('filter.show_filter')" />
          </div>
        </div>
        <SFCategoryNavigationSlider
          v-if="rootCategories?.length"
          :all-categories="flattenCategoryTree(rootCategories)"
          :current-category="currentCategory"
          class="mb-3.5 mt-2.5 md:hidden"
        />
        <SFFilterToggleButton
          :label="$t('filter.show_filter_and_sorting')"
          class="md:hidden"
        />
        <SFPrevPagination
          v-if="prevPageToLoad"
          :page-to-load="prevPageToLoad"
          :total-count="totalProductsCount"
        />
        <SFProductList
          :current-category="currentCategory"
          :is-pagination-visible="false"
          :loading="productsStatus"
          :pagination="pagination"
          :products="products"
          class="mt-6"
          @click-product="handleProductClick"
          @intersect:row="trackViewListing"
        />

        <SFPagination
          v-if="pagination"
          :count="productsCount"
          :page-to-load="nextPageToLoad"
          :total-count="totalProductsCount"
        />
        <CMSPostListingPage
          v-if="isFirstPageLoadedOnly"
          :category-id="currentCategoryId"
          :slug="pathWithoutLocale"
          class="mt-20 w-full"
        />
      </div>
    </div>
    <SFFilterSlideIn
      :category="currentCategory"
      :current-category-id="currentCategoryId"
      :products-count="totalProductsCount"
    />
    <Teleport to="#teleports">
      <div class="fixed bottom-8 right-4 md:bottom-24">
        <SFScrollToTopButton />
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  hydrateOnVisible,
  onBeforeUnmount,
  onMounted,
  onServerPrefetch,
  ref,
  watch,
} from 'vue'
import {
  type Category,
  HttpStatusCode,
  type Product,
  sanitizeCanonicalURL,
} from '@scayle/storefront-nuxt'
import { definePageMeta, useHead, useSeoMeta } from '#imports'
import { useRoute, useRouter } from '#app/composables/router'
import { useNuxtApp } from '#app/nuxt'
import {
  useBreadcrumbs,
  useJsonld,
  usePageState,
  useProductsByCategory,
  useTrackingEvents,
} from '~/composables'
import { useProductClickStorage } from '~/composables/useProductClickStorage'

import { createError } from '#app/composables/error'
import {
  generateCategoryBreadcrumbSchema,
  getBrand,
  getCategoryId,
} from '~/utils'
import { hasOnlyFilter, shouldFollowRobots } from '~/utils/filter'
import { categoryListingMetaData, PRODUCTS_PER_PAGE } from '~/constants'
import CMSProductListingPageTeaser from '#storefront-cms/components/ProductListingPageTeaser.vue'
import SFCategorySideNavigation from '~/components/category/SFCategorySideNavigation.vue'
import SFCategoryNavigationBackButton from '~/components/category/SFCategoryNavigationBackButton.vue'
import SFCategoryBreadcrumbs from '~/components/category/SFCategoryBreadcrumbs.vue'
import SFSortSelection from '~/components/sorting/SFSortSelection.vue'
import SFFilterToggleButton from '~/components/filter/SFFilterToggleButton.vue'
import SFCategoryNavigationSlider from '~/components/category/SFCategoryNavigationSlider.vue'
import SFProductList from '~/components/productList/SFProductList.vue'
import SFFilterSlideIn from '~/components/filter/SFFilterSlideIn.vue'
import SFScrollToTopButton from '~/components/SFScrollToTopButton.vue'
import { SFPagination } from '#storefront-ui/components'
import {
  flattenCategoryTree,
  useAppliedFilters,
} from '#storefront-product-listing'
import {
  useAvailableShops,
  useCategoryById,
  useCurrentShop,
} from '#storefront/composables'
import { usePLPSEO } from '~/composables/usePLPSEO'
import { useCMSBySlug } from '~/modules/cms/providers/contentful/composables/useCMS'
import type { TypeListingPageSkeleton } from '~/modules/cms/providers/contentful/types'
import SFPrevPagination from '#storefront-ui/components/pagination/SFPrevPagination.vue'
import { useGenderMenuItems } from '~/composables/useGenderMenuItems'

const CMSPostListingPage = defineAsyncComponent({
  loader: () => import('#storefront-cms/components/PostListingPage.vue'),
  hydrate: hydrateOnVisible(),
})

const router = useRouter()
const route = useRoute()
const { $config } = useNuxtApp()

const { setPageState } = usePageState()

const { trackViewItemList, trackSelectItem } = useTrackingEvents()
const { saveProductClickFromBrandList } = useProductClickStorage()

const normalizedOptions = computed(() =>
  import.meta.server ? { lazy: false, immediate: true } : {},
)

const { rootCategories, fetchingCategories } = defineProps<{
  rootCategories: Category[]
  fetchingCategories: boolean
}>()
const currentCategoryId = computed(() => getCategoryId(route.params))

const categoryParams = computed(() => ({
  id: currentCategoryId.value,
  children: 0,
  properties: { withName: ['sale'] },
}))

const categoryPromise = await useCategoryById(
  {
    params: categoryParams,
    options: normalizedOptions.value,
  },
  `plp-current-category`,
)

const {
  data: currentCategory,
  status: categoryStatus,
  error: categoryError,
} = categoryPromise

const { appliedFilter } = useAppliedFilters(route)
const currentPage = computed(() => Number(route.query.page) || 1)

const {
  products,
  fetching: productsStatus,
  totalProductsCount,
  paginationOffset,
  pagination,
  productsCount,
  prevPageToLoad,
  nextPageToLoad,
  loadedPages,
} = await useProductsByCategory(currentCategory, {
  options: normalizedOptions.value,
  params: { includeSoldOut: false },
})
const isFirstPageLoadedOnly = computed(
  () => currentPage.value === 1 && loadedPages.value.size === 1,
)

const currentBrand = computed(() => getBrand(route, products))

// Manual scroll position management for proper back button restoration
const saveScrollPosition = () => {
  const scrollY = window.scrollY
  const key = `scroll_${route.path}_page_${currentPage.value}`
  sessionStorage.setItem(key, scrollY.toString())
}

const restoreScrollPosition = () => {
  const key = `scroll_${route.path}_page_${currentPage.value}`
  const savedPosition = sessionStorage.getItem(key)
  if (savedPosition) {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: parseInt(savedPosition, 10),
          behavior: 'instant',
        })
      })
    })
  }
}

// Save scroll position before navigating to PDP
const handleProductClick = (
  product: Product,
  _: any,
  link: string | undefined,
) => {
  saveScrollPosition()
  trackProductClick(product, link)
}

// Restore scroll position when coming back from PDP
onMounted(() => {
  // Wait a bit for products to load and render
  setTimeout(() => {
    restoreScrollPosition()
  }, 200)
})

// Save scroll position periodically and before leaving
let scrollSaveInterval: number | undefined
onMounted(() => {
  // Save scroll position every 2 seconds while user is scrolling
  scrollSaveInterval = window.setInterval(saveScrollPosition, 2000)
  // Also save on scroll events (throttled)
  let scrollTimeout: number | undefined
  const handleScroll = () => {
    clearTimeout(scrollTimeout)
    scrollTimeout = window.setTimeout(saveScrollPosition, 100)
  }
  window.addEventListener('scroll', handleScroll, { passive: true })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
    clearTimeout(scrollTimeout)
  })
})

onBeforeUnmount(() => {
  if (scrollSaveInterval) {
    clearInterval(scrollSaveInterval)
  }
  saveScrollPosition() // Save one last time
})

// Validates that the category exists and redirects to the correct path if the URL is incorrect but the category ID is valid
// If an error occurs or the category is not found, a 404 error is thrown also redirecting to the correct path if the category ID is valid but the path is incorrect
const validateCategoryExistsAndRedirect = async () => {
  if (categoryStatus.value == 'pending') {
    return
  }

  if (categoryError.value || !currentCategory.value) {
    throw createError({ statusCode: HttpStatusCode.NOT_FOUND, fatal: true })
  }
}

onServerPrefetch(validateCategoryExistsAndRedirect)

const trackProductClick = (product: Product, link: string | undefined) => {
  if (!product?.id) {
    return
  }

  // Save to localStorage
  saveProductClickFromBrandList(product, {
    categoryName: currentCategory.value?.name || 'Unknown',
  })

  trackSelectItem({
    product,
    listingMetaData: categoryListingMetaData,
    pagePayload: {
      resultsCount: pagination.value?.total || 0,
      totalPages: Math.ceil(
        (totalProductsCount.value || 0) / PRODUCTS_PER_PAGE,
      ),
      pageSize: currentPage.value * PRODUCTS_PER_PAGE,
      currentPage: pagination.value?.page || currentPage.value,
      label: 'Brand Products List',
    },
  })

  if (link) {
    router.push(link)
  }
}

const trackViewListing = ({
  items,
}: {
  row: number
  items: (Product & { index: number })[]
}) => {
  // Only track if we have valid items and category ID
  if (!items?.length || !currentCategoryId.value) {
    return
  }

  // Add location information to each item
  const itemsWithLocation = items.map((item) => ({
    ...item,
    item_list_id: 'Brand products list',
    item_list_name: `Brand products list: ${
      currentCategory.value?.name || 'Unknown'
    }`,
  }))

  trackViewItemList({
    items: itemsWithLocation,
    listingMetaData: {
      id: 'Brand products list',
      name: `Brand products list: ${currentCategory.value?.name || 'Unknown'}`,
    },
    paginationOffset: paginationOffset.value || 0,
    pagePayload: {
      label: 'Brand products list',
      resultsCount: pagination.value?.total || 0,
      totalPages: Math.ceil(
        (totalProductsCount.value || 0) / PRODUCTS_PER_PAGE,
      ),
      pageSize: currentPage.value * PRODUCTS_PER_PAGE,
      currentPage: pagination.value?.page || currentPage.value,
    },
  })
}

// Track initial product load
const hasTrackedInitialLoad = ref(false)
watch(
  products,
  (newProducts) => {
    // Track when products are first loaded (not just when page changes)
    if (newProducts?.length && !hasTrackedInitialLoad.value) {
      hasTrackedInitialLoad.value = true
      // Small delay to ensure products are properly rendered
      setTimeout(() => {
        trackViewListing({
          row: 0,
          items: newProducts.map((product, index) => ({ ...product, index })),
        })
      }, 100)
    }
  },
  { immediate: true },
)

// Reset tracking flag when route changes
watch(
  () => route.path,
  () => {
    hasTrackedInitialLoad.value = false
  },
)

watch(
  [currentCategory, () => route.path],
  async ([category]) => {
    await validateCategoryExistsAndRedirect()
    setPageState('typeId', String(category?.id))
  },
  { immediate: true },
)

const { getBreadcrumbsFromCategory } = useBreadcrumbs()
const breadcrumbs = computed(() =>
  currentCategory.value
    ? getBreadcrumbsFromCategory(currentCategory.value, true)
    : [],
)

const categoryBreadcrumbSchema = computed(() =>
  generateCategoryBreadcrumbSchema(breadcrumbs.value),
)
const followRobots = computed(() => {
  const filters = appliedFilter.value?.attributes || []

  return shouldFollowRobots(filters, totalProductsCount.value)
})

const robots = computed(() => {
  if (totalProductsCount.value === 0) {
    return 'noindex'
  }
  if (followRobots.value) {
    return 'index, follow'
  } else {
    return 'noindex, follow'
  }
})
const currentShop = useCurrentShop()
const { genderCategoriesMap } = useGenderMenuItems()

const canonicalLink = computed(() => {
  if (robots.value.includes('noindex') || currentPage.value > 1) {
    return []
  }

  const CATEGORY_PREFIX = 'b'

  const parentCategoryPath = currentCategory.value?.path
    .split('/')
    .filter(
      (item) =>
        Boolean(item) &&
        item !== 'brands' &&
        item !== currentCategory.value?.slug,
    )
    .join('/')

  const parentCategorySlugFragment = parentCategoryPath
    ? `${parentCategoryPath}/`
    : ''

  let href = `${$config.public.baseUrl}/${currentShop.value.path}/${CATEGORY_PREFIX}/${parentCategorySlugFragment}${currentCategory.value?.slug}-${currentCategory.value?.id}`

  const category = pathWithoutLocale.value?.replace(`${CATEGORY_PREFIX}/`, '')
  const gender = category.split('-')[0]

  if (genderCategoriesMap.value[gender]) {
    href = sanitizeCanonicalURL(
      `${$config.public.baseUrl}/${currentShop.value.path}/${gender}/`,
    )
  }

  if (
    hasOnlyFilter(appliedFilter.value?.attributes ?? [], 'brand') ||
    hasOnlyFilter(appliedFilter.value?.attributes ?? [], 'color')
  ) {
    href = `${$config.public.baseUrl}${route.fullPath}`
  }

  return [
    {
      rel: 'canonical',
      key: 'canonical',
      href,
    },
  ]
})

useJsonld(() => categoryBreadcrumbSchema.value as any)

usePLPSEO(currentCategory, currentBrand)

const pathWithoutLocale = computed(() => {
  const routePath = route.path.replace(/^\/[a-z]{2}-[a-z]{2}/, '')
  return routePath.startsWith('/') ? routePath.slice(1) : routePath
})
const availableShops = useAvailableShops()

const currentShopLocale = computed(() => {
  return (
    availableShops.value.find(
      (shop) => shop.locale === currentShop.value.locale,
    )?.locale ?? 'en-GB'
  )
})

const { data: cmsData } = useCMSBySlug<TypeListingPageSkeleton>(
  `listingPage-${pathWithoutLocale.value}`,
  {
    'fields.slug': pathWithoutLocale.value,
    content_type: 'listingPage',
    // @ts-expect-error Type mismatch, from Contentful Core implementation
    locale: currentShopLocale.value,
  },
)

const ogImage = computed(() => {
  const firstImageProduct = products.value?.[0]?.images?.[0]
  const cmsImage =
    cmsData.value?.fields?.seo?.fields?.ogImage?.fields?.file?.url
  return cmsImage ? `https:${cmsImage}` : firstImageProduct?.hash
})

useSeoMeta({
  robots: () => robots.value,
  ogImage: () => ogImage.value,
  ogUrl: () => canonicalLink?.value?.[0]?.href,
})

useHead(() => {
  return {
    link: [...canonicalLink.value],
  }
})

defineOptions({ name: 'CategoryPage' })

// The 'key' is required to preserve component state across PLP navigation.
// Without it, components like 'SFCategorySideNavigation' are re-rendered on every route change,
// leading to focus loss and unnecessary performance overhead.
definePageMeta({
  pageType: 'category_page',
  key: 'PLP',
  contentType: 'Brand Page',
  SnPageType: 'brand',
})
</script>
